import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { fetchJobById, fetchJobsOffers } from '../lib/api';
import { Job } from '../types/jobs';
import { ArrowLeft, Building2, MapPin, DollarSign, Clock, Calendar, CheckCircle, Globe, Award, ExternalLink, Briefcase, MailCheck } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { SEO } from '../components/SEO';
import { openWebRedirect } from '../lib/api';
import * as amplitude from '@amplitude/analytics-browser';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, userInfo } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = React.useState<Job | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [relatedJobs, setRelatedJobs] = React.useState<Job[]>([]);
  const hasTrackedRef = React.useRef(false);

  // Redirect to home if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  React.useEffect(() => {
    if (!hasTrackedRef.current && job) {
      // Analytics tracking for job detail view
      amplitude.track("Job Detail View", {
        jobId: job.id,
        jobTitle: job.title || 'Unknown job',
        featured: job.featured || false
      });
      hasTrackedRef.current = true;
    }
  }, [job]);

  // Function to safely decode HTML and convert BBCode
  const decodeHTML = (html?: string) => {
    if (!html) return '';
    try {
      // First decode HTML entities
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      let content = txt.value;

      // Then convert BBCode to HTML
      // Replace [COLOR=value]text[/COLOR]
      content = content.replace(/\[COLOR=([^\]]+)\]([\s\S]*?)\[\/COLOR\]/g, (match, color, text) => {
        // Handle variables in color values like var(--tw-prose-bold)
        if (color.includes('--tw-prose-bold')) {
          return `<strong>${text}</strong>`;
        }
        // Other color values
        return `<span style="color: ${color};">${text}</span>`;
      });

      // Replace [B] or [BOLD]
      content = content.replace(/\[B\]([\s\S]*?)\[\/B\]/gi, '<strong>$1</strong>');
      content = content.replace(/\[BOLD\]([\s\S]*?)\[\/BOLD\]/gi, '<strong>$1</strong>');

      // Replace [I] or [ITALIC]
      content = content.replace(/\[I\]([\s\S]*?)\[\/I\]/gi, '<em>$1</em>');
      content = content.replace(/\[ITALIC\]([\s\S]*?)\[\/ITALIC\]/gi, '<em>$1</em>');

      // Replace [U] or [UNDERLINE]
      content = content.replace(/\[U\]([\s\S]*?)\[\/U\]/gi, '<u>$1</u>');
      content = content.replace(/\[UNDERLINE\]([\s\S]*?)\[\/UNDERLINE\]/gi, '<u>$1</u>');
      
      // Replace [SIZE=value]text[/SIZE]
      content = content.replace(/\[SIZE=([^\]]+)\]([\s\S]*?)\[\/SIZE\]/gi, (match, size, text) => {
        // Convert size to pixels or relative unit
        return `<span style="font-size: ${size};">${text}</span>`;
      });
      
      // Replace [URL=url]text[/URL]
      content = content.replace(/\[URL=([^\]]+)\]([\s\S]*?)\[\/URL\]/gi, '<a href="$1" target="_blank" rel="noopener noreferrer">$2</a>');
      
      // Replace [URL]url[/URL]
      content = content.replace(/\[URL\]([\s\S]*?)\[\/URL\]/gi, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');

      return content;
    } catch (e) {
      console.error('HTML decode error:', e);
      return html || '';
    }
  };
  
  // Format publication date with improved error handling
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non spécifiée';
    
    try {
      // First check if the string is a valid date format
      const parsedDate = parseISO(dateString);
      if (!isValid(parsedDate)) {
        return 'Non spécifiée';
      }
      return format(parsedDate, 'd MMMM yyyy', { locale: fr });
    } catch (e) {
      console.error('Date formatting error:', e);
      return 'Non spécifiée';
    }
  };

  // Get employer display name
  const getEmployerDisplay = (employer: any): string => {
    if (typeof employer === 'object' && employer !== null && !Array.isArray(employer)) {
      // Handle the case where employer is an object with a name property
      return employer.name || "Employeur non spécifié";
    } else if (Array.isArray(employer) && employer.length > 0) {
      // Handle the case where employer is an array with content
      if (typeof employer[0] === 'string') {
        return employer[0];
      } else if (typeof employer[0] === 'object' && employer[0]?.name) {
        return employer[0].name;
      }
    } else if (typeof employer === 'string' && employer) {
      // Handle the case where employer is a string
      return employer;
    }
    
    // Fallback for empty arrays or null/undefined employers
    return "Employeur non spécifié";
  };

  React.useEffect(() => {
    const fetchJob = async () => {
      if (!id) return;

      setIsLoading(true);
      setError(null);

      try {
        const jobData = await fetchJobById(id, userInfo);
        setJob(jobData);
        
        // Fetch related jobs (could be from same category or employer)
        try {
          const relatedJobsData = await fetchJobsOffers(`/jobs?limit=3&category=${jobData.category || 0}`, userInfo);
          if (Array.isArray(relatedJobsData)) {
            // Filter out the current job
            setRelatedJobs(relatedJobsData.filter(j => j.id !== id));
          } else if (relatedJobsData.data && Array.isArray(relatedJobsData.data)) {
            setRelatedJobs(relatedJobsData.data.filter(j => j.id !== id));
          }
        } catch (error) {
          console.error('Error fetching related jobs:', error);
        }
      } catch (error) {
        console.error('Error fetching job:', error);
        setError('Impossible de charger cette offre d\'emploi');
      } finally {
        setIsLoading(false);
      }
    };

    if (user && id) {
      fetchJob();
    }
  }, [id, user, userInfo]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <SEO 
        title={job ? `${decodeHTML(job.title)} - Offre d'emploi PVT` : "Détail d'offre d'emploi - pvtistes.net"}
        description={job ? `Détails de l'offre d'emploi "${decodeHTML(job.title)}"${job.location ? ` à ${job.location}` : ''}. Postulez maintenant!` : "Consultez cette offre d'emploi pour PVTistes"}
        image="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=2574&auto=format&fit=crop"
      />
      
      <Navbar 
        onLogin={() => {}} 
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      <div className="fixed top-16 left-0 right-0 z-40 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={() => navigate('/jobs')}
              className="flex items-center gap-2 px-3 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Retour aux offres</span>
            </button>
          </div>
        </div>
      </div>

      <main className="flex-1 pt-28 sm:pt-32 pb-24 sm:pb-12">
        {isLoading ? (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <div className="animate-pulse space-y-6 sm:space-y-8">
              {/* Job title skeleton */}
              <div className="h-7 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4 max-w-lg" />
              
              {/* Company info skeleton */}
              <div className="h-5 sm:h-6 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2 max-w-xs" />
              
              {/* Job details skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-24 sm:h-28 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                ))}
              </div>
              
              {/* Description skeleton */}
              <div className="space-y-3 sm:space-y-4 mt-6">
                <div className="h-6 sm:h-7 bg-gray-200 dark:bg-gray-700 rounded-lg w-48" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/6" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
              </div>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 sm:p-8 text-center">
              <h2 className="text-xl font-semibold text-red-900 dark:text-red-300 mb-4">
                {error}
              </h2>
              <p className="text-red-600 dark:text-red-400 mb-6">
                Cette offre n'est peut-être plus disponible ou a été supprimée.
              </p>
              <div className="mt-2">
                <button
                  onClick={() => navigate('/jobs')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                >
                  <span>Retour aux offres d'emploi</span>
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : job ? (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
              {/* Premium/Featured banner */}
              {(job.featured || job.employerVerified) && (
                <div className={`w-full py-2 sm:py-3 px-4 text-center text-white ${
                  job.featured 
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600' 
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                }`}>
                  <div className="flex items-center justify-center gap-2">
                    {job.featured ? (
                      <>
                        <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base font-medium">Offre Premium</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base font-medium">Employeur vérifié</span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Job header */}
              <div className="p-5 sm:p-8 pb-4 sm:pb-6 border-b border-gray-100 dark:border-gray-700/30">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3 leading-tight">
                      {decodeHTML(job.title)}
                    </h1>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
                      <span className="text-base sm:text-lg font-medium text-gray-700 dark:text-gray-300">
                        {getEmployerDisplay(job.employer)}
                      </span>
                    </div>
                  </div>
                  
                  {job.applyUrl && (
                    <a
                      href={job.applyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        // Still track the click event
                        amplitude.track('Job Apply From Detail', {
                          jobId: job.id,
                          jobTitle: job.title || 'Unknown job',
                          featured: job.featured || false
                        });
                        // Use openWebRedirect instead of direct window.open
                        openWebRedirect(job.applyUrl, userInfo);
                      }}
                      className={`flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3 text-white font-medium rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-[1.03] ${
                        job.featured 
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' 
                          : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700'
                      }`}
                    >
                      <span className="text-sm sm:text-base">Postuler</span>
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  )}
                </div>
              </div>
              
              {/* Key details - Vertical stack */}
              <div className="px-5 sm:px-8 py-5 sm:py-6 border-b dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100/70 dark:border-gray-700/30 transform transition-all duration-300 hover:shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="p-2 sm:p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow-sm">
                        <MapPin className="w-5 h-5 text-blue-500 dark:text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Lieu</div>
                        <div className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mt-1">
                          {job.location || 'Non spécifié'}
                          {job.locationFlexible === 1 && (
                            <span className="ml-2 text-xs sm:text-sm text-blue-500 dark:text-blue-400 font-normal inline-flex items-center gap-1.5">
                              <Globe className="w-3.5 h-3.5" />
                              <span>Flexible</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100/70 dark:border-gray-700/30 transform transition-all duration-300 hover:shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="p-2 sm:p-2.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg shadow-sm">
                        <DollarSign className="w-5 h-5 text-emerald-500 dark:text-emerald-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Salaire</div>
                        <div className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mt-1">
                          {job.salaryFull || 
                           (job.salary && (
                             `${job.salary} ${job.salaryCurrency || ''}${job.salaryPeriod ? ` ${job.salaryPeriod === 'hourly' ? 'par heure' : 
                             job.salaryPeriod === 'weekly' ? 'par semaine' : 
                             job.salaryPeriod === 'monthly' ? 'par mois' : 
                             job.salaryPeriod === 'yearly' ? 'par an' : 
                             job.salaryPeriod}` : ''}`
                           )) || 'Non spécifié'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100/70 dark:border-gray-700/30 transform transition-all duration-300 hover:shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="p-2 sm:p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg shadow-sm">
                        <Clock className="w-5 h-5 text-amber-500 dark:text-amber-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Date de début</div>
                        <div className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mt-1">
                          {job.startASAP ? 'Dès que possible' : 
                           (job.startDate ? formatDate(job.startDate) : 'Non spécifiée')}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100/70 dark:border-gray-700/30 transform transition-all duration-300 hover:shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="p-2 sm:p-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-lg shadow-sm">
                        <Calendar className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Publié le</div>
                        <div className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mt-1">
                          {formatDate(job.published)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Job description */}
              <div className="p-5 sm:p-8">
                <div className="mb-8">
                  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-5 flex items-center gap-3">
                    <div className="p-1.5 sm:p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                      <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500 dark:text-primary-400" />
                    </div>
                    <span>Description du poste</span>
                  </h2>
                  <div 
                    className="prose prose-lg dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 
                              prose-headings:font-semibold 
                              prose-p:text-gray-700 dark:prose-p:text-gray-300
                              prose-strong:text-gray-900 dark:prose-strong:text-white
                              prose-li:text-gray-700 dark:prose-li:text-gray-300
                              prose-a:text-primary-600 dark:prose-a:text-primary-400"
                    dangerouslySetInnerHTML={{ 
                      __html: decodeHTML(job.description) || 'Aucune description disponible' 
                    }}
                  />
                </div>

                {/* Apply button - bottom */}
                {job.applyUrl && (
                  <div className="mt-8 pt-6 border-t dark:border-gray-700/50 flex flex-col sm:flex-row items-center gap-4 justify-between">
                    <div className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
                      <p>Cette offre vous intéresse ? Postulez directement ci-dessous.</p>
                    </div>
                    <a
                      href={job.applyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.preventDefault();
                        // Still track the click event
                        amplitude.track('Job Apply From Detail', {
                          jobId: job.id,
                          jobTitle: job.title || 'Unknown job',
                          featured: job.featured || false
                        });
                        // Use openWebRedirect instead of direct window.open
                        openWebRedirect(job.applyUrl, userInfo);
                      }}
                      className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 text-white font-medium rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                        job.featured 
                          ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' 
                          : 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700'
                      }`}
                    >
                      <span>Postuler maintenant</span>
                      <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
                    </a>
                  </div>
                )}

                {/* Job alerts CTA */}
                <div className="mt-8 pt-6 border-t dark:border-gray-700/50">
                  <div className="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 rounded-xl p-4 sm:p-6 border border-primary-100/50 dark:border-primary-800/30 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <div className="p-2.5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm flex-shrink-0">
                        <MailCheck className="w-5 h-5 sm:w-6 sm:h-6 text-primary-500" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                          Ne manquez aucune opportunité !
                        </h3>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                          Recevez les nouvelles offres d'emploi directement dans votre boîte mail
                        </p>
                      </div>
                      <div className="w-full sm:w-auto flex-shrink-0 mt-3 sm:mt-0">
                        <a 
                          href="https://pvtistes.net/jobs/index.php?do=notifications" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm hover:shadow-md cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            amplitude.track("Job Notifications Click");
                            openWebRedirect("https://pvtistes.net/jobs/index.php?do=notifications", userInfo);
                          }}
                        >
                          <span>S'inscrire aux alertes</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Related jobs - moved higher up to avoid mobile menu overlap */}
            {relatedJobs.length > 0 && (
              <div className="mt-8 sm:mt-10 mb-10 sm:mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-5 px-1">
                  Offres similaires
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {relatedJobs.slice(0, 3).map(relatedJob => (
                    <div 
                      key={relatedJob.id} 
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md border border-gray-200/50 dark:border-gray-700/50 p-4 cursor-pointer transition-all duration-200 hover:-translate-y-1"
                      onClick={() => navigate(`/jobs/${relatedJob.id}`)}
                    >
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                        {decodeHTML(relatedJob.title)}
                      </h3>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
                        <Building2 className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm font-medium truncate">{getEmployerDisplay(relatedJob.employer)}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 mb-2">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-xs truncate">{relatedJob.location || 'Lieu non spécifié'}</span>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/30 flex justify-between items-center">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Publié le {formatDate(relatedJob.published)}
                        </span>
                        <div className="text-primary-600 dark:text-primary-400 text-sm font-medium flex items-center gap-1">
                          <span>Voir</span>
                          <ArrowLeft className="w-3.5 h-3.5 transform rotate-180" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 sm:p-8 text-center">
              <h2 className="text-xl font-semibold text-red-900 dark:text-red-300 mb-4">
                Offre non trouvée
              </h2>
              <p className="text-red-600 dark:text-red-400 mb-6">
                Cette offre n'est peut-être plus disponible ou a été supprimée.
              </p>
              <div className="mt-2">
                <button
                  onClick={() => navigate('/jobs')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                >
                  <span>Retour aux offres d'emploi</span>
                  <ArrowLeft className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}